import React, { useCallback, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import apiLibrary from "services/api";
import { useNavigate, useParams } from "react-router-dom";
import { Toasts } from "view/components/Toasts";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { closeAddOrEditSpecieModal } from "store/modals/reducer.actions";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { specieSchema } from "utils/validationSchemas";
import SpecieModalForm from "./speciesModalForm";
import * as Yup from "yup";
import { fetchSpeciesAction } from "store/species";
import { fetchSpecieProfileAction } from "store/specieProfile/reducer.actions";
import usePermissions from "hooks/usePermissions";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#ffff",
  borderRadius: "10px",
};
interface FormValues {
  alternativeName?: string;
  commonName: string;
  description: string;
  images: number[];
  isStar?: number;
  scientificName?: string;
  typeId: number;
  preferredName: string;
}

interface FormItem {
  value: string;
  label: string;
}

interface FormItem {
  value: string;
  label: string;
}

/**
 * Component for adding, editing, or using a form as a template.
 * @returns {JSX.Element} The component's JSX representation
 */

export const AddOrEditSpecieModal = () => {
  // State variables

  const { addOrEditSpecieModal } = useSelector(
    (state: RootState) => state.modals
  );
  const { communityId } = useParams();

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [typesList, setTypesList] = useState([]);
  const { types } = usePermissions();

  const fetchAllTypes = useCallback(
    async (searchQuery?: string) => {
      if (types.canViewDropdown) {
        try {
          const res =
            await apiLibrary.SpeciesAndObjects.getTypesDropdown(searchQuery);
          setTypesList(
            res.data.map((type: any) => ({
              label: type?.typeName,
              value: type?.id,
            }))
          );
        } catch (error: any) {
          // Handle API errors
          console.error("Error fetching categories:", error);
        }
      }
    },
    [typesList]
  );

  const handleTypeSearch = (query: string) => {
    fetchAllTypes(query);
  };

  // Fetch Protocol Profile and Programs on component mount
  useEffect(() => {
    if (addOrEditSpecieModal.isOpen) {
      fetchAllTypes();
    }
  }, [addOrEditSpecieModal.isOpen]);

  /**
   * Handle form submission.
   * @param {object} values - Form values
   * @param {Function} setSubmitting - Function to set submitting state
   * @example
   * handleSubmit({ name: "Form Name" }, { setSubmitting: (bool) => {} });
   */

  const ACCEPTED_FILES_TYPES = {
    "image/jpeg": [".jpg", ".jpeg", ".png"],
  };
  const uploadFile = async (image: any) => {
    if (!image.id) {
      const res = await apiLibrary.file.fileUpload(image.file);
      return { id: res.data.id, isStar: image.isStar };
    }
    return { id: image.id, isStar: image.isStar };
  };
  // SUBMIT HANDLER
  const handleSubmit = async (
    values: any,
    {
      setSubmitting,
      setFieldError,
    }: { setSubmitting: (isSubmitting: boolean) => void; setFieldError: any }
  ) => {
    setIsLoading(true);

    try {
      const formValues: FormValues = {
        commonName: values.commonName,
        description: values.description,
        images: [],
        alternativeName: values.alternativeName,
        scientificName: values.scientificName,
        typeId: values.typeId.value,
        preferredName: values.preferredName,
      };

      if (values?.images?.length > 0) {
        const uploadFilePromises = values.images.map((image: any) =>
          uploadFile(image)
        );

        let uploadResults = await Promise.all(uploadFilePromises);
        //   if (uploadResults.length > 3) {
        //     let rearrangedResults = uploadResults.slice(0, 3); // First 3 images remain as is
        //     for (let i = 3; i < uploadResults.length; i++) {
        //         rearrangedResults.splice(-i + 2, 0, uploadResults[i]);
        //     }
        //     uploadResults = rearrangedResults;
        // }

        formValues.images = uploadResults.map((result) => result.id);
        const starredImage = uploadResults.find((result) => result.isStar);
        if (starredImage) {
          formValues.isStar = starredImage.id;
        }
      }

      if (addOrEditSpecieModal.editMode && addOrEditSpecieModal.data.id) {
        const { data, message } = await apiLibrary.SpeciesAndObjects.editSpecie(
          addOrEditSpecieModal.data.id,
          formValues
        );
        if (addOrEditSpecieModal.data.path === "directory") {
          dispatch(fetchSpeciesAction(communityId));
        } else if (addOrEditSpecieModal.data.path === "profilePage") {
          dispatch(fetchSpecieProfileAction(addOrEditSpecieModal.data.id));
        }
        Toasts.success(message);
        handleClose();
      } else {
        const { data, message } =
          await apiLibrary.SpeciesAndObjects.addSpecie(formValues);
        // if (addOrEditSpecieModal.data.id) {
        //   addOrEditSpecieModal?.data?.connectedEntitiesList?.push({
        //     id: data?.id,
        //   });
        //   const { message } =
        //     await apiLibrary.Communities.addExistingSpeciesToCommunity(
        //       addOrEditSpecieModal.data.id,
        //       {
        //         specieIds:
        //           addOrEditSpecieModal?.data?.connectedEntitiesList?.map(
        //             (entity: any) => {
        //               return entity.id;
        //             }
        //           ),
        //       }
        //     );
        //   Toasts.success(message);
        // }

        navigate(`/species/${data.id}/profile`);

        Toasts.success(message);
        handleClose();
      }
    } catch (error: any) {
      // Handle API errors
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // HANDLE MODAL CLOSE
  const handleClose = () => {
    if (!isLoading) {
      dispatch(closeAddOrEditSpecieModal());
    }
  };

  return (
    <Modal
      open={addOrEditSpecieModal.isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      disableAutoFocus={true}
      aria-describedby="modal-modal-description"
      className="border-none"
      sx={{
        ".MuiBox-root": {
          border: "none",
          outline: "none",
        },
      }}
    >
      <Box sx={style} className="dark:bg-secondaryLight ">
        <Formik
          initialValues={addOrEditSpecieModal.data}
          validationSchema={specieSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {(formikProps) => (
            <Form>
              <SpecieModalForm
                {...formikProps}
                handleClose={handleClose}
                acceptedFileTypes={ACCEPTED_FILES_TYPES}
                editMode={addOrEditSpecieModal.editMode}
                typesList={typesList}
                handleTypeSearch={handleTypeSearch}
              />
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
